import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { trackIntegrationEvent } from '@modules/integrationsV2/utils';
import { LoadingButton } from '@mui/lab';
import { DialogActions, DialogContent, Stack, TextField } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDirection,
  IntegrationType,
} from 'generated/graphql';
import { ChangeEvent, useState } from 'react';
import useCreateIntegrationFormData from 'v2/core/services/integrations';
import { useGetUser } from 'v2/core/services/user';

interface FreshdeskFormModalProps {
  integrationType: IntegrationType;
}

export const FreshdeskFormModal = ({
  integrationType,
}: FreshdeskFormModalProps) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();
  const { closeModal } = useMuiModalContext();
  const { showErrorNotification } = useNotificationContext();

  const [domain, setDomain] = useState('');
  const [apiKey, setApiKey] = useState('');

  const { loading, createIntegration } = useCreateIntegrationFormData({
    tenantID,
    userID,
    state: IntegrationChannelType.FreshDesk,
    direction: IntegrationDirection.Inbound,
    integrationType,
    subDomain: domain,
    code: apiKey,
    onError: (error: Error) => {
      showErrorNotification({
        contentComponent: `Something went wrong${
          error.message ? `: ${error.message}` : '!'
        }`,
        props: {},
      });
    },
    onCompleted: () => {
      closeModal();
      trackIntegrationEvent({
        eventName: 'Integration Added',
        integrationChannelType: IntegrationChannelType.FreshDesk,
      });
    },
  });

  const handleSubmit = () => {
    createIntegration();
    trackIntegrationEvent({
      eventName: 'Integration Form Connect CTA Clicked',
      integrationChannelType: IntegrationChannelType.FreshDesk,
    });
  };

  const handleDomainInputField = (event: ChangeEvent<HTMLInputElement>) => {
    setDomain(event.target.value);
  };

  const handleAPIKeyInputField = (event: ChangeEvent<HTMLInputElement>) => {
    setApiKey(event.target.value);
  };

  return (
    <>
      <ModalHeader title="Freshdesk" showCloseButton={true} />
      <DialogContent dividers>
        <Stack pt={1} spacing={2.5}>
          <TextField
            required
            fullWidth
            label="Domain"
            placeholder="Enter Domain"
            variant="outlined"
            onChange={handleDomainInputField}
            disabled={loading}
          />
          <TextField
            required
            fullWidth
            label="API Key"
            placeholder="Enter API key"
            variant="outlined"
            onChange={handleAPIKeyInputField}
            disabled={loading}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <LoadingButton
          variant="contained"
          type="submit"
          size="large"
          fullWidth
          onClick={handleSubmit}
          loading={loading}
          disabled={!domain.length || !apiKey.length}
        >
          Connect
        </LoadingButton>
      </DialogActions>
    </>
  );
};
